import React, {
  ReactElement,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Route, RouteComponentProps } from 'react-router-dom';
import { connectHits } from 'react-instantsearch-dom';
import axios from 'axios';

import { isArray, isEqual } from 'lodash';
import {
  ALGOLIA_BREEDER_INDEX,
  PROFILES_ADMIN_PREFIX,
  PROFILES_BROWSER_API_PREFIX,
} from '../../../../utils/constants';
import AdminDashboardTableHits from '../../admin-dashboard-tables/AdminDashboardTableHits';
import { SellerSearchState, SellerProfile, UserData } from '../../admin-dashboard-tables/interfaces';
import SellerURLSync from '../../SellerURLSync';
import useModal from '../../../../utils/hooks/useModal';
import ConfirmationModal from '../../widgets/ConfirmationModal';
import ProfileHit from './ProfileHit';

const Hits: React.FC<{
  hits: any,
}> = ({ hits }) => {
  return (
    <div className="profile-table">
      <AdminDashboardTableHits
        hits={hits}
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        Hit={Hit}
        selected={[]}
        toggleSelected={() => {}}
      />
    </div>
  );
};

const Hit: React.FC<SellerProfile> = ({
  name,
  id,
  profile_photo_url,
  slug,
  route,
  phone,
  primary_name,
  primary_phone,
  primary_method,
}) => {
  const [isHidden, setIsHidden] = useState(false);
  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: `Would you like to delete this profile: ${name}?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.patch(`${PROFILES_BROWSER_API_PREFIX}/${id}`, {
                publication_status: 'unpublished',
              })
                .then(({ data }) => {
                  res(data.message || 'Profile deleted.');
                })
                .catch((e) => {
                  const errorMessages: string[] = [];
                  // Get errors from response.
                  const catchErrors = e.response?.data?.errors;

                  Object.values(catchErrors)
                    .forEach((catchError: any) => {
                      Object.values(catchError)
                        .forEach((messages) => {
                          if (isArray(messages)) {
                            messages.forEach((message) => {
                              errorMessages.push(message);
                            });
                          }
                        });
                    });

                  // Build individual error messages.
                  // Otherwise, return traditional bulk error message
                  rej(errorMessages.join('. ') || e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  };
  return (
    <>
      {!isHidden && (
        <ProfileHit
          key={`${name}-${id}`}
          id={id}
          profile_photo_url={profile_photo_url}
          name={name}
          slug={slug}
          route={route}
          phone={phone}
          primary_name={primary_name}
          primary_phone={primary_phone}
          primary_method={primary_method}
          onDelete={() => updateModal('delete')}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

const CustomHits = connectHits(Hits);

interface attrToLock {
  name: string,
  value: any,
  type: 'numeric'|'facet'|'query',
}

interface SellerProfilesSearch extends RouteComponentProps {
  user: UserData,
}
const SingleSellerProfiles: React.FC<SellerProfilesSearch> = ({
  history,
  location,
  match,
  user,
}): ReactElement => {
  const [externalSearchState, setExternalSearchState] = useState<SellerSearchState>({
    configure: {
      facetFilters: user.seller_status === 'approved' ? ['publication_status:published'] : [],
      numericFilters: user.id ? [`user_id = ${user.id}`, 'deprecated = 0'] : ['deprecated = 0'],
    },
    query: '',
  });

  useEffect(() => {
    const { id } = user;
    const { configure } = externalSearchState;
    if (configure) {
      const { numericFilters } = configure;
      if (!isEqual(numericFilters, [`user_id = ${id}`])) {
        setExternalSearchState({
          ...externalSearchState,
          configure: {
            ...externalSearchState.configure,
            numericFilters: [`user_id = ${id}`, 'deprecated = 0'],
          },
        });
      }
    }
  }, [user]);

  const attributesToLock: Array<attrToLock> = [
    {
      name: 'user_id',
      value: `user_id = ${user.id}`,
      type: 'numeric',
    },
    {
      name: 'publication_status',
      value: 'publication_status:published',
      type: 'facet',
    },
  ];

  return (
    <>
      {(!user || !user.id)
        ? (
          <div className="alert alert-danger" role="alert">
            <h2>Error</h2>
            <p>Could not load profiles. Check your connection and try again.</p>
          </div>
        )
        : (
          <>
            <SellerURLSync
              bulkActions={[]}
              externalQueryParams={[]}
              history={history}
              location={location}
              match={match}
              externalSearchState={externalSearchState}
              tableHeaderRowValues={[]}
              indexName={ALGOLIA_BREEDER_INDEX!}
              customHits={<CustomHits />}
              attributes={[]}
              dateAttributes={[]}
              tabs={[]}
              useCards={true}
              allowSetHitsPerPage={false}
              currentUser={user}
              attributesToLock={
                user.seller_status === 'approved'
                  ? [
                    ...attributesToLock,
                    {
                      name: 'publication_status',
                      value: 'publication_status:published',
                      type: 'facet',
                    },
                  ] : attributesToLock
              }
              showSearch={true}
            />
          </>
        )}
    </>
  );
};

export default SingleSellerProfiles;

if (document.getElementById('react-seller-dashboard-table-profiles')) {
  const element = document.getElementById('react-seller-dashboard-table-profiles');
  // const dataEl = document.getElementById('react-seller-user-data');
  const { user } = window;
  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={`${PROFILES_ADMIN_PREFIX}`}
        component={(props) => <SingleSellerProfiles user={user} {...props} />}
      />
    </BrowserRouter>,
    element,
  );
}
